import React, { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PASS_KEY = "apothecary_admin_auth";
const DEFAULT_PASS = process.env.REACT_APP_APOTHECARY_PASS || "BlindApothecary!";

const statusOptions = ["New", "In Progress", "Ready", "Completed", "Delivered", "Cancelled"];
const discountOptions = [
  { value: "Standard", label: "None" },
  { value: "Guildie", label: "Guildy (20%)" },
  { value: "Friend", label: "Friend (30%)" },
  { value: "Custom", label: "Custom…" },
];

const rarityOptions = ["Common", "Uncommon", "Rare", "Heroic", "Epic", "Legendary"];
const ALL_RARITIES = rarityOptions;
const tierOptions = ["Novice", "Apprentice", "Journeyman", "Master", "Grand Master"];
const categories = ["Herbalism", "Alchemy", "Scribing"];

function formatCopper(copper) {
  if (!copper || copper <= 0) return "0c";
  const gold = Math.floor(copper / 10000);
  const rem = copper % 10000;
  const silver = Math.floor(rem / 100);
  const c = rem % 100;
  const parts = [];
  if (gold) parts.push(`${gold}g`);
  if (silver) parts.push(`${silver}s`);
  if (c || !parts.length) parts.push(`${c}c`);
  return parts.join(" ");
}

function parseCurrency(input) {
  if (!input) return 0;
  const tokens = input.split(/\s+/).filter(Boolean);
  let gold = 0;
  let silver = 0;
  let copper = 0;
  for (const token of tokens) {
    const m = token.match(/^(\d+)([gsc])$/i);
    if (!m) continue;
    const value = parseInt(m[1], 10) || 0;
    const unit = m[2].toLowerCase();
    if (unit === "g") gold = value;
    if (unit === "s") silver = value;
    if (unit === "c") copper = value;
  }
  return gold * 10000 + silver * 100 + copper;
}

function slugifyId(value) {
  return (value || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 60);
}

export default function AdminPage() {
  const [authorized, setAuthorized] = useState(() => localStorage.getItem(PASS_KEY) === "1");
  const [passInput, setPassInput] = useState("");

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);

  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  const [catalog, setCatalog] = useState([]);
  const [recipeEditorItem, setRecipeEditorItem] = useState(null); // full item object for pop-out

  const [catalogLoading, setCatalogLoading] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [debugItemId, setDebugItemId] = useState("");
  const [debugResult, setDebugResult] = useState(null);
  const [debugLoading, setDebugLoading] = useState(false);
  const [trueCostSweep, setTrueCostSweep] = useState(null);
  const [trueCostSweepRunning, setTrueCostSweepRunning] = useState(false);
  const [materialCalcItemId, setMaterialCalcItemId] = useState("");
  const [materialCalcQty, setMaterialCalcQty] = useState("1");
  const [materialCalcResult, setMaterialCalcResult] = useState(null);
  const [materialCalcError, setMaterialCalcError] = useState("");
  const [materialCalcLoading, setMaterialCalcLoading] = useState(false);
  const [materialCalcCategory, setMaterialCalcCategory] = useState("All");
  const [materialCalcSearch, setMaterialCalcSearch] = useState("");
  const [materialCalcFull, setMaterialCalcFull] = useState(false);
  const [showIngredientCosts, setShowIngredientCosts] = useState(true);
  const [newIngredientName, setNewIngredientName] = useState("");
  const [newIngredientCost, setNewIngredientCost] = useState("");
  const [pendingDeletedIngredients, setPendingDeletedIngredients] = useState([]);
  const [editingReagentRarity, setEditingReagentRarity] = useState("");



  const [itemSearch, setItemSearch] = useState("");

  const [itemEditor, setItemEditor] = useState(null); // { mode, id, category, tier, name, labor_minutes, bench_copper, full_service_display }

  const loadOrders = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/apothecary/admin/orders`);
      setOrders(res.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      const res = await axios.get(`${API}/apothecary/admin/settings`);
      setSettings(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const loadCatalog = async () => {
    setCatalogLoading(true);
    try {
      // Use admin items endpoint so we can see disabled/hidden items in the admin UI
      const res = await axios.get(`${API}/apothecary/admin/items`);
      setCatalog(res.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setCatalogLoading(false);
    }
  };

  useEffect(() => {
    if (authorized) {
      loadOrders();
      loadSettings();
      loadCatalog();
    }
  }, [authorized]);

  const authorize = () => {
    if (passInput.trim() === DEFAULT_PASS) {
      localStorage.setItem(PASS_KEY, "1");
      setAuthorized(true);
    } else {
      alert("Incorrect passcode");
    }
  };

  const logout = () => {
    localStorage.removeItem(PASS_KEY);
    setAuthorized(false);
  };

  const updateOrder = async (id, updates) => {
    try {
      await axios.patch(`${API}/apothecary/admin/orders/${id}`, updates);
      await loadOrders();
    } catch (e) {
      console.error(e);
      alert("Failed to update order");
    }
  };

  const handleSettingsChange = (field, value) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
  };

  const toggleTierDisabled = (category, tier) => {
    setSettings((prev) => {
      const current = prev?.disabled_tiers || {};
      const existing = new Set(current[category] || []);
      if (existing.has(tier)) {
        existing.delete(tier);
      } else {
        existing.add(tier);
      }
      return {
        ...prev,
        disabled_tiers: {
          ...current,
          [category]: Array.from(existing),
        },
      };
    });
  };

  const saveSettings = async () => {
    if (!settings) return;
    setSavingSettings(true);

    // Apply pending reagent deletions on save: drop them from ingredient_costs payload
    const effectiveIngredientCosts = Object.fromEntries(
      Object.entries(settings.ingredient_costs || {}).filter(
        ([name]) => !pendingDeletedIngredients.includes(name),
      ),
    );

    try {
      await axios.patch(`${API}/apothecary/admin/settings`, {
        labor_rate_cpm: Number(settings.labor_rate_cpm),
        markup_pct: Number(settings.markup_pct),
        customer_notification: settings.customer_notification || "",
        disabled_rarities: settings.disabled_rarities || {},
        disabled_tiers: settings.disabled_tiers || {},
        disabled_items: settings.disabled_items || [],
        item_overrides: settings.item_overrides || {},
        custom_items: settings.custom_items || [],
        removed_items: settings.removed_items || [],
        item_meta_overrides: settings.item_meta_overrides || {},
        full_service_overrides: settings.full_service_overrides || {},
        ingredient_costs: effectiveIngredientCosts,
        item_markup_overrides: settings.item_markup_overrides || {},
        reagent_rarity_pricing_enabled: settings.reagent_rarity_pricing_enabled || false,
        reagent_rarity_costs: settings.reagent_rarity_costs || {},
        // New: Apothecary Discord orders channel id
        discord_orders_channel_id: settings.discord_orders_channel_id || "",
      });

      await loadSettings();
      await loadCatalog();
      setPendingDeletedIngredients([]);
    } catch (e) {
      console.error(e);
    }
  };

  const runMaterialCalc = async () => {
    if (!materialCalcItemId) return;
    const qty = Math.max(1, parseInt(materialCalcQty || "1", 10) || 1);

    setMaterialCalcLoading(true);
    setMaterialCalcError("");
    setMaterialCalcResult(null);

    try {
      const res = await axios.get(`${API}/apothecary/admin/true_cost_debug/${materialCalcItemId}`);
      const data = res.data || {};
      if (data.error) {
        setMaterialCalcError(typeof data.error === "string" ? data.error : String(data.error));
        return;
      }
      const breakdown = data.breakdown;
      if (!breakdown || !breakdown.ingredients) {
        setMaterialCalcError("No TRUE COST breakdown available for this item.");
        return;
      }

      const reagents = new Map();
      const crafts = new Map();

      const walk = (node, mult, depth) => {
        if (!node || !Array.isArray(node.ingredients)) return;
        for (const ing of node.ingredients) {
          const qtyLocal = Number(ing.qty ?? 1) * mult;
          if (ing.kind === "reagent") {
            const name = ing.name || ing.item_id || "(unknown reagent)";
            reagents.set(name, (reagents.get(name) || 0) + qtyLocal);
          } else if (ing.kind === "craft") {
            const name = ing.name || ing.item_id || "(unknown craft)";
            crafts.set(name, (crafts.get(name) || 0) + qtyLocal);
            // Only recurse into sub-recipes when full tree is requested
            if (materialCalcFull && ing.details) {
              walk(ing.details, qtyLocal, depth + 1);
            }
          }
        }
      };

      // depth=0 for top-level; recursion only if materialCalcFull is true
      walk(breakdown, qty, 0);

      setMaterialCalcResult({
        reagents: Array.from(reagents.entries()).map(([name, totalQty]) => ({ name, qty: totalQty })),
        crafts: Array.from(crafts.entries()).map(([name, totalQty]) => ({ name, qty: totalQty })),
      });
    } catch (e) {
      console.error(e);
      setMaterialCalcError(e.response?.data?.detail || "Failed to run materials calculator");
    } finally {
      setMaterialCalcLoading(false);
    }
  };

  const runTrueCostSweep = () => {
    if (!settings || !catalog.length) return;

    setTrueCostSweepRunning(true);

    // Use setTimeout to allow UI to update with loading state
    setTimeout(() => {
      const missingBench = [];

      for (const it of catalog) {
        if (it.category !== "Alchemy" && it.category !== "Scribing") continue;

        // TRUE COST bench comes from the dynamic recipe, not the legacy/base bench
        const dyn = (settings.recipes || {})[it.id];
        if (!dyn) continue; // items without TRUE COST recipes are already surfaced by the "Missing recipes" view

        const bench = Number(dyn.bench ?? 0);
        if (!bench) {
          missingBench.push({ id: it.id, name: it.name, category: it.category, tier: it.tier });
        }
      }

      setTrueCostSweep({ missingBench });
      setTrueCostSweepRunning(false);
    }, 100);
  };

  const runTrueCostDebug = async () => {
    if (!debugItemId) return;
    setDebugLoading(true);
    try {
      const res = await axios.get(`${API}/apothecary/admin/true_cost_debug/${debugItemId}`);
      setDebugResult(res.data);
    } catch (e) {
      console.error(e);
      setDebugResult({ error: e.response?.data?.detail || "Failed to run TRUE COST debug" });
    } finally {
      setDebugLoading(false);
    }
  };

  if (!authorized) {
    return (
      <div className="max-w-md mx-auto bg-[#201713] border border-amber-800/60 rounded-xl p-6 mt-8">
        <h2 className="text-xl font-semibold text-amber-100 mb-2">Apothecary Admin</h2>
        <p className="text-sm text-amber-200/80 mb-4">Enter the passcode to manage orders and settings.</p>
        <label className="block text-sm text-amber-200 mb-1">Passcode</label>
        <input
          type="password"
          value={passInput}
          onChange={(e) => setPassInput(e.target.value)}
          className="w-full bg-[#2a1d17] border border-amber-800 text-amber-100 rounded px-2 py-1 mb-3"
        />
        <button
          onClick={authorize}
          className="inline-flex items-center justify-center rounded-md bg-amber-700 hover:bg-amber-600 text-black px-4 py-2 text-sm font-semibold"
        >
          Enter
        </button>
      </div>
    );
  }

  const activeOrders = orders.filter((o) => o.status !== "Completed" && o.status !== "Delivered");
  const completedOrders = orders.filter((o) => o.status === "Completed" || o.status === "Delivered");

  // Build settings content separately to avoid JSX nesting issues
  let settingsContent;
  if (!settings) {
    settingsContent = <p className="text-amber-200 text-sm">Loading settings…</p>;
  } else {
    settingsContent = (
      <div className="space-y-4 text-sm">
        {/* Labor / markup */}
        <div className="space-y-3">
          <div>
            <label className="block text-amber-200 mb-1">Labor rate (copper per minute)</label>
            <input
              type="number"
              value={settings.labor_rate_cpm}
              onChange={(e) => handleSettingsChange("labor_rate_cpm", e.target.value)}
              className="w-full bg-[#17110e] border border-[#4b3624] text-amber-100 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-amber-500"
            />
          </div>
          <div>
            <label className="block text-amber-200 mb-1">Markup (%)</label>
            <input
              type="number"
              step="0.01"
              value={settings.markup_pct}
              onChange={(e) => handleSettingsChange("markup_pct", e.target.value)}
              className="w-full bg-[#17110e] border border-[#4b3624] text-amber-100 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-amber-500"
            />
          </div>
        </div>

        {/* Discord routing for Apothecary orders */}
        <div className="pt-3 border-t border-amber-900/60 space-y-2">
          <div>
            <label className="block text-amber-200 mb-1">Orders Discord Channel ID</label>
            <input
              type="text"
              value={settings.discord_orders_channel_id || ""}
              onChange={(e) => handleSettingsChange("discord_orders_channel_id", e.target.value)}
              placeholder="e.g. 1452434688235802654"
              className="w-full bg-[#17110e] border border-[#4b3624] text-amber-100 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-amber-500 text-xs"
            />
            <p className="text-[11px] text-amber-300/80 mt-1">
              Paste the numeric ID of the channel in your Apothecary Discord server where new orders should create threads.
            </p>
          </div>
        </div>

        {/* Reagent / flora prices */}
        <div className="pt-3 border-t border-amber-900/60 space-y-2">
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => setShowIngredientCosts((prev) => !prev)}
              className="flex items-center gap-2 text-amber-200 font-semibold text-sm"
            >
              <span>{showIngredientCosts ? "▼" : "▶"}</span>
              <span>Reagent &amp; flora costs</span>
            </button>
            <p className="text-amber-300/80 text-xs">
              Set per-reagent base costs in copper. These are used when computing TRUE COST for recipes.
            </p>
            <label className="inline-flex items-center gap-2 text-[11px] text-amber-300/80 mt-1">
              <input
                type="checkbox"
                checked={settings.reagent_rarity_pricing_enabled || false}
                onChange={(e) =>
                  handleSettingsChange("reagent_rarity_pricing_enabled", e.target.checked)
                }
                className="h-3 w-3"
              />
              <span>Enable rarity-specific reagent costs</span>
            </label>
          </div>

          {showIngredientCosts && (
            <>
              <div className="flex items-center gap-2 text-xs mt-1">
                <input
                  type="text"
                  value={newIngredientName}
                  onChange={(e) => setNewIngredientName(e.target.value)}
                  placeholder="Add reagent or flora name (case-insensitive)"
                  className="flex-1 bg-[#17110e] border border-[#4b3624] text-amber-100 rounded px-2 py-1 text-xs"
                />
                <input
                  type="number"
                  min="0"
                  value={newIngredientCost}
                  onChange={(e) => setNewIngredientCost(e.target.value)}
                  placeholder="Cost (c)"
                  className="w-28 bg-[#17110e] border border-[#4b3624] text-amber-100 rounded px-2 py-1 text-xs"
                />
                <button
                  type="button"
                  onClick={() => {
                    const name = newIngredientName.trim();
                    const raw = newIngredientCost.trim();
                    if (!name || !raw) return;
                    const cost = Number(raw || 0);
                    if (isNaN(cost) || cost < 0) return;
                    handleSettingsChange("ingredient_costs", {
                      ...(settings.ingredient_costs || {}),
                      [name]: cost,
                    });
                    setNewIngredientName("");
                    setNewIngredientCost("");
                  }}
                  className="px-3 py-1 rounded-md bg-amber-700 hover:bg-amber-600 text-black text-xs font-semibold"
                >
                  Add
                </button>
              </div>

              <div className="max-h-40 overflow-auto border border-amber-900/60 rounded-md bg-black/20 p-2 text-xs space-y-1 mt-2">
                {settings.ingredient_costs && (
                  <>
                    {pendingDeletedIngredients.length > 0 && (
                      <p className="text-[11px] text-amber-300/70 mb-1">
                        Removed reagents are marked and will be forgotten once you save settings.
                      </p>
                    )}
                    {Object.entries(settings.ingredient_costs)
                      .sort(([a,], [b,]) => a.localeCompare(b))
                      .map(([name, value]) => {
                      const isPendingDelete = pendingDeletedIngredients.includes(name);
                      const rarCosts = (settings.reagent_rarity_costs || {})[name] || {};
                      return (
                        <div
                          key={name}
                          className={`flex flex-col gap-1 border-b border-amber-900/40 pb-1 mb-1 ${
                            isPendingDelete ? "opacity-50 line-through" : ""
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <div className="flex-1 min-w-[120px] truncate text-amber-100">{name}</div>
                            <input
                              type="number"
                              min="0"
                              value={value}
                              disabled={isPendingDelete}
                              onChange={(e) => {
                                const v = Number(e.target.value || 0);
                                handleSettingsChange("ingredient_costs", {
                                  ...(settings.ingredient_costs || {}),
                                  [name]: v,
                                });
                              }}
                              className="w-24 bg-[#17110e] border border-[#4b3624] text-amber-100 rounded px-2 py-1 text-xs"
                            />
                            {settings.reagent_rarity_pricing_enabled && !isPendingDelete && (
                              <button
                                type="button"
                                onClick={() =>
                                  setEditingReagentRarity((prev) =>
                                    prev === name ? "" : name,
                                  )
                                }
                                className="px-2 py-0.5 text-[11px] rounded-full border border-amber-700 text-amber-200 hover:bg-amber-900/40"
                              >
                                {editingReagentRarity === name ? "Hide rarity" : "Edit rarity"}
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => {
                                // Mark for deletion; actual removal happens on Save settings
                                setPendingDeletedIngredients((prev) =>
                                  prev.includes(name) ? prev : [...prev, name],
                                );
                              }}
                              className="px-2 py-0.5 text-[11px] rounded-full border border-red-700 text-red-300 hover:bg-red-900/40"
                            >
                              Remove
                            </button>
                          </div>

                          {settings.reagent_rarity_pricing_enabled &&
                            !isPendingDelete &&
                            editingReagentRarity === name && (
                              <div className="pl-2 grid grid-cols-2 gap-1 text-[11px] text-amber-200">
                                {ALL_RARITIES.map((rar) => (
                                  <label
                                    key={`${name}-${rar}`}
                                    className="flex items-center justify-between gap-1"
                                  >
                                    <span>{rar}:</span>
                                    <input
                                      type="number"
                                      min="0"
                                      value={
                                        rarCosts[rar] ?? (settings.ingredient_costs?.[name] ?? 0)
                                      }
                                      onChange={(e) => {
                                        const v = Number(e.target.value || 0);
                                        setSettings((prev) => {
                                          const current = prev.reagent_rarity_costs || {};
                                          const per = { ...(current[name] || {}) };
                                          per[rar] = v;
                                          return {
                                            ...prev,
                                            reagent_rarity_costs: { ...current, [name]: per },
                                          };
                                        });
                                      }}
                                      className="w-20 bg-[#17110e] border border-[#4b3624] text-amber-100 rounded px-2 py-0.5 text-[11px]"
                                    />
                                  </label>
                                ))}
                              </div>
                            )}
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            </>
          )}
        </div>

        {/* TRUE COST debug inspector */}
        <div className="pt-3 border-t border-amber-900/60 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-amber-200 font-semibold text-sm">TRUE COST debug inspector</span>
            <span className="text-amber-300/80 text-xs">Inspect bench, reagents, labor & markup, or scan for missing wiring</span>
          </div>
          <div className="flex flex-col gap-2 text-xs">
            <div className="flex items-center gap-2">
              <select
                className="flex-1 bg-[#17110e] border border-[#4b3624] text-amber-100 rounded px-2 py-1 text-xs"
                value={debugItemId}
                onChange={(e) => setDebugItemId(e.target.value)}
              >
                <option value="">Select an item…</option>
                {catalog
                  .filter((it) => it.category === "Alchemy" || it.category === "Scribing")
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((it) => (
                    <option key={it.id} value={it.id}>
                      {it.name} ({it.category})
                    </option>
                  ))}
              </select>
              <button
                type="button"
                onClick={runTrueCostDebug}
                disabled={!debugItemId || debugLoading}
                className="px-3 py-1 rounded-md bg-amber-700 hover:bg-amber-600 text-black text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {debugLoading ? "Running…" : "Run debug"}
              </button>
            </div>
            <div className="flex items-center justify-between gap-2">
              <p className="text-amber-300/80 text-[11px]">
                Quick sweep: find Alchemy &amp; Scribing items with TRUE COST recipes but missing TRUE COST bench.
              </p>
              <button
                type="button"
                onClick={runTrueCostSweep}
                disabled={trueCostSweepRunning}
                className="px-3 py-1 rounded-md bg-amber-700 hover:bg-amber-600 text-black text-[11px] font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {trueCostSweepRunning ? "Scanning…" : "Scan all"}
              </button>
            </div>
          </div>

          {trueCostSweep && (
            <div className="mt-2 border border-amber-900/60 rounded-md bg-black/30 p-2 text-[11px] space-y-2 max-h-56 overflow-auto">
              {trueCostSweep.missingBench?.length ? (
                <div>
                  <p className="font-semibold text-amber-200 mb-1">Missing TRUE COST bench (Alchemy &amp; Scribing)</p>
                  <ul className="space-y-0.5">
                    {trueCostSweep.missingBench.map((it) => (
                      <li key={`bench-${it.id}`} className="flex justify-between gap-2">
                        <span className="truncate">{it.name} ({it.category} {it.tier})</span>
                        <span className="text-amber-300/80 font-mono">{it.id}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <p className="text-emerald-300">All Alchemy &amp; Scribing items with TRUE COST recipes have bench values.</p>
              )}

              {trueCostSweep.missingRecipe?.length ? (
                <div>
                  <p className="font-semibold text-amber-200 mt-2 mb-1">Missing TRUE COST recipe</p>
                  <ul className="space-y-0.5">
                    {trueCostSweep.missingRecipe.map((it) => (
                      <li key={`recipe-${it.id}`} className="flex justify-between gap-2">
                        <span className="truncate">{it.name} ({it.category} {it.tier})</span>
                        <span className="text-amber-300/80 font-mono">{it.id}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <p className="text-emerald-300 mt-1">All Alchemy &amp; Scribing items have a TRUE COST recipe.</p>
              )}
            </div>
          )}

          {debugResult && (
            <div className="mt-2 border border-amber-900/60 rounded-md bg-black/30 p-2 text-[11px] space-y-1 max-h-56 overflow-auto">
              {debugResult.error && (
                <p className="text-red-300">Error: {debugResult.error}</p>
              )}
              {!debugResult.error && (
                <>
                  <div className="flex justify-between gap-2">
                    <span className="text-amber-100 font-semibold truncate">{debugResult.item_name}</span>
                    <span className="text-amber-300/80">{debugResult.category} – {debugResult.tier}</span>
                  </div>
                  <p className="text-amber-300/80">Recipe source: {debugResult.recipe_source}</p>
                  {debugResult.pricing && (
                    <div className="mt-1 space-y-0.5">
                      <p>Labor cost: {debugResult.pricing.labor_cost}c</p>
                      <p>Markup: {(debugResult.pricing.markup_pct * 100).toFixed(1)}%</p>
                      <p>Base (true cost + labor): {debugResult.pricing.base_cost_before_markup}c</p>
                      <p>Full-service (no mats): {debugResult.pricing.full_service_price_no_mats}c</p>
                      <p>Mats provided price: {debugResult.pricing.mats_provided_price}c</p>
                    </div>
                  )}
                  {debugResult.breakdown && (
                    <div className="mt-2">
                      {debugResult.breakdown.missing_reagents && debugResult.breakdown.missing_reagents.length > 0 && (
                        <p className="text-[11px] text-red-300 mb-1">
                          Missing reagent costs for: {debugResult.breakdown.missing_reagents.join(", ")}
                        </p>
                      )}
                      <p className="font-semibold text-amber-200 mb-1">Materials breakdown</p>
                      <p>Bench: {debugResult.breakdown.bench}c</p>
                      {debugResult.breakdown.ingredients && debugResult.breakdown.ingredients.length > 0 && (
                        <ul className="mt-1 space-y-0.5">
                          {debugResult.breakdown.ingredients.map((ing, idx) => (
                            <li key={idx} className="flex justify-between gap-2">
                              <span className="truncate">
                                {ing.kind === "reagent" ? "Reagent" : "Craft"}: {ing.name || ing.item_id} × {ing.qty}
                              </span>
                              <span>
                                {ing.unit_cost}c ea → {ing.total_cost}c
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Per-item overrides + item manager */}

        <div className="pt-3 border-t border-amber-900/60 space-y-3">
          {/* Tier visibility controls */}
          <div className="flex flex-wrap items-center gap-3 text-xs">
            <span className="text-amber-200">Tier visibility:</span>
            {categories.map((cat) => (
              <div key={cat} className="flex items-center gap-1">
                <span className="text-amber-300/80 mr-1">{cat}:</span>
                {tierOptions.map((tier) => {
                  const disabledForCat = new Set(settings.disabled_tiers?.[cat] || []);
                  const checked = disabledForCat.has(tier);
                  return (
                    <label
                      key={`${cat}-${tier}`}
                      className="inline-flex items-center gap-0.5 text-[10px] text-amber-200/80 mr-1"
                    >
                      <input
                        type="checkbox"
                        checked={!checked}
                        onChange={() => toggleTierDisabled(cat, tier)}
                        className="h-3 w-3"
                      />
                      <span>{tier}</span>
                    </label>
                  );
                })}
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between mt-2">
            <span className="text-amber-200 font-semibold text-sm">Per-item price overrides</span>
            <span className="text-amber-300/80 text-xs">Browse by category and fine-tune prices</span>
          </div>
          <p className="text-amber-300/80 text-xs">
            Set a specific base price for individual catalog items using gold / silver / copper (e.g. &quot;10g 50s 25c&quot;).
            Leave blank to remove the override and fall back to tier defaults.
          </p>

          <div className="flex items-center justify-between gap-2 text-xs">
            <div className="flex items-center gap-2">
              <span className="text-amber-200">Category:</span>
              <button
                type="button"
                onClick={() => setCategoryFilter("All")}
                className={`px-2 py-1 rounded-full border text-xs ${
                  categoryFilter === "All"
                    ? "bg-amber-700 text-black border-amber-500"
                    : "bg-[#2a1d17] text-amber-200 border-amber-800 hover:bg-amber-900/40"
                }`}
              >
                All
              </button>
              {categories.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setCategoryFilter(cat)}
                  className={`px-2 py-1 rounded-full border text-xs ${
                    categoryFilter === cat
                      ? "bg-amber-700 text-black border-amber-500"
                      : "bg-[#2a1d17] text-amber-200 border-amber-800 hover:bg-amber-900/40"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <span className="text-amber-200">Add / edit items:</span>
              <button
                type="button"
                onClick={() => {
                  const baseCategory = categoryFilter === "All" ? "Herbalism" : categoryFilter;
                  setItemEditor({
                    mode: "add",
                    id: null,
                    category: baseCategory,
                    tier: tierOptions[0],
                    name: "",
                    labor_minutes: "",
                    bench_copper: "",
                    full_service_display: "",
                  });
                }}
                className="inline-flex items-center gap-1 rounded-md bg-amber-700 hover:bg-amber-600 text-black px-3 py-1 text-xs font-semibold"
              >
                + New item
              </button>
            </div>
          </div>

          <div className="flex items-center gap-2 text-xs mt-2">
          <div className="flex items-center gap-2 text-xs mt-1">
            <span className="text-amber-200">Show:</span>
            <button
              type="button"
              onClick={() => setCategoryFilter("MissingRecipes")}
              className={`px-2 py-0.5 rounded-full border text-[11px] ${
                categoryFilter === "MissingRecipes"
                  ? "bg-red-900/40 text-red-200 border-red-600"
                  : "bg-[#2a1d17] text-amber-200 border-amber-800 hover:bg-amber-900/40"
              }`}
            >
              Missing recipes
            </button>
          </div>

            <span className="text-amber-200">Search:</span>
            <input
              type="text"
              value={itemSearch}
              onChange={(e) => setItemSearch(e.target.value)}
              placeholder="Type to filter items by name or id"
              className="flex-1 bg-[#17110e] border border-[#4b3624] text-amber-100 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-amber-500"
            />
          </div>

          <div className="space-y-1 max-h-56 overflow-auto pr-1 border border-amber-900/60 rounded-md p-2 bg-black/20">
            {catalog
              .filter((item) => {
                if (categoryFilter === "MissingRecipes") {
                  const hasDynamic = Boolean(settings.recipes?.[item.id]);
                  // We don't know static recipes on the client, so "missing" means no dynamic recipe yet
                  return item.category !== "Herbalism" && !hasDynamic;
                }
                return categoryFilter === "All" || item.category === categoryFilter;
              })
              .filter((item) => {
                if (!itemSearch.trim()) return true;
                const q = itemSearch.toLowerCase();
                return (
                  item.name.toLowerCase().includes(q) ||
                  item.id.toLowerCase().includes(q)
                );
              })
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((item) => {
                const rawOverride = settings.item_overrides?.[item.id];
                let overrideDisplay = "";
                if (typeof rawOverride === "number" && rawOverride > 0) {
                  const gold = Math.floor(rawOverride / 10000);
                  const rem = rawOverride % 10000;
                  const silver = Math.floor(rem / 100);
                  const copper = rem % 100;
                  const parts = [];
                  if (gold) parts.push(`${gold}g`);
                  if (silver) parts.push(`${silver}s`);
                  if (copper || !parts.length) parts.push(`${copper}c`);
                  overrideDisplay = parts.join(" ");
                }

                const isDisabledItem = (settings.disabled_items || []).includes(item.id);
                const isRemovedItem = (settings.removed_items || []).includes(item.id);

                const handleEdit = () => {
                  // Open recipe/item editor pop-out with full item context
                  setRecipeEditorItem(item);
                };

                const handleRemove = () => {
                  const removed = settings.removed_items || [];
                  const isCustom = (settings.custom_items || []).some((ci) => ci.id === item.id);

                  if (isCustom) {
                    // For custom items, treat Remove as a true delete so the id can be reused later
                    const nextCustom = (settings.custom_items || []).filter((ci) => ci.id !== item.id);
                    handleSettingsChange("custom_items", nextCustom);
                  } else {
                    // For base items, keep existing behavior: add id to removed_items ban-list
                    if (removed.includes(item.id)) return;
                    handleSettingsChange("removed_items", [...removed, item.id]);
                  }
                };

                return (
                  <div
                    key={item.id}
                    className={`flex items-center gap-2 text-xs ${
                      isRemovedItem ? "opacity-50 line-through" : ""
                    }`}
                  >
                    <div className="flex-1 min-w-[200px]">
                      <div className="text-amber-100 font-semibold truncate">{item.name}</div>
                      <div className="text-amber-300/80">
                        {item.category} – {item.tier}
                      </div>
                    </div>
                    <label className="inline-flex items-center gap-1 text-[11px] text-amber-200/80">
                      <input
                        type="checkbox"
                        checked={isDisabledItem}
                        disabled={isRemovedItem}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          const current = settings.disabled_items || [];
                          const next = checked
                            ? Array.from(new Set([...current, item.id]))
                            : current.filter((id) => id !== item.id);
                          handleSettingsChange("disabled_items", next);
                        }}
                      />
                      <span>Hide</span>
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. 10g 50s 25c"
                      className="w-28 bg-[#2a1d17] border border-amber-800 text-amber-100 rounded px-2 py-1"
                      value={overrideDisplay}
                      disabled={isRemovedItem}
                      onChange={(e) => {
                        const copper = parseCurrency(e.target.value);
                        handleSettingsChange("item_overrides", {
                          ...(settings.item_overrides || {}),
                          [item.id]: copper || undefined,
                        });
                      }}
                    />
                    {/* Badge to show if item has TRUE COST recipe */}
                    {settings.recipes?.[item.id] && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full border border-emerald-600 text-emerald-300 bg-emerald-900/30">
                        TRUE COST
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={handleEdit}
                      className="px-2 py-0.5 rounded-full border border-amber-700 text-amber-200 hover:bg-amber-900/40"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={handleRemove}
                      className="px-2 py-0.5 rounded-full border border-red-700 text-red-300 hover:bg-red-900/40"
                    >
                      Remove
                    </button>
                  </div>
                );
              })}
          </div>

          {itemEditor && (
            <div className="mt-3 border border-amber-900/60 rounded-md bg-black/30 p-3 text-xs space-y-2">
              <div className="flex items-center justify-between mb-1">
                <span className="text-amber-200 font-semibold">
                  {itemEditor.mode === "add" ? "Add new item" : "Edit item"}
                </span>
                <button
                  type="button"
                  onClick={() => setItemEditor(null)}
                  className="text-amber-300 hover:text-amber-100"
                >
                  Close
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <label className="block text-amber-200 mb-1">Category</label>
                  <select
                    value={itemEditor.category}
                    onChange={(e) => setItemEditor((prev) => ({ ...prev, category: e.target.value }))}
                    className="w-full bg-[#17110e] border border-[#4b3624] text-amber-100 rounded px-2 py-1 text-xs"
                  >
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-amber-200 mb-1">Tier</label>
                  <select
                    value={itemEditor.tier}
                    onChange={(e) => setItemEditor((prev) => ({ ...prev, tier: e.target.value }))}
                    className="w-full bg-[#17110e] border border-[#4b3624] text-amber-100 rounded px-2 py-1 text-xs"
                  >
                    {tierOptions.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-amber-200 mb-1">Name</label>
                  <input
                    type="text"
                    value={itemEditor.name || ""}
                    onChange={(e) => setItemEditor((prev) => ({ ...prev, name: e.target.value }))}
                    className="w-full bg-[#17110e] border border-[#4b3624] text-amber-100 rounded px-2 py-1 text-xs"
                  />
                </div>
                <div>
                  <label className="block text-amber-200 mb-1">Item ID</label>
                  <div className="text-amber-100 text-xs bg-[#17110e] border border-[#4b3624] rounded px-2 py-1 break-all">
                    {slugifyId(itemEditor.name || "new_item")}
                  </div>
                </div>
                <div>
                  <label className="block text-amber-200 mb-1">Labor time (minutes)</label>
                  <input
                    type="number"
                    min="0"
                    value={itemEditor.labor_minutes || ""}
                    onChange={(e) => setItemEditor((prev) => ({ ...prev, labor_minutes: e.target.value }))}
                    className="w-full bg-[#17110e] border border-[#4b3624] text-amber-100 rounded px-2 py-1 text-xs"
                  />
                </div>
                <div>
                  <label className="block text-amber-200 mb-1">Bench cost (copper)</label>
                  <input
                    type="number"
                    min="0"
                    value={itemEditor.bench_copper || ""}
                    onChange={(e) => setItemEditor((prev) => ({ ...prev, bench_copper: e.target.value }))}
                    className="w-full bg-[#17110e] border border-[#4b3624] text-amber-100 rounded px-2 py-1 text-xs"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-amber-200 mb-1">Full-service price (no mats, e.g. 10g 50s 25c)</label>
                  <input
                    type="text"
                    value={itemEditor.full_service_display || ""}
                    onChange={(e) => setItemEditor((prev) => ({ ...prev, full_service_display: e.target.value }))}
                    className="w-full bg-[#17110e] border border-[#4b3624] text-amber-100 rounded px-2 py-1 text-xs"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => setItemEditor(null)}
                  className="px-3 py-1 rounded-md border border-amber-700 text-amber-200 text-xs hover:bg-amber-900/40"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    const category = itemEditor.category;
                    const tier = itemEditor.tier || tierOptions[0];
                    const name = itemEditor.name || "New item";
                    const id = itemEditor.mode === "add" ? (slugifyId(name) || slugifyId("new_item")) : itemEditor.id;

                    // Prevent creating duplicate items with the same id
                    if (itemEditor.mode === "add") {
                      const existingIds = new Set([
                        ...(settings.custom_items || []).map((ci) => ci.id),
                        ...catalog.map((it) => it.id),
                      ]);
                      if (existingIds.has(id)) {
                        alert("Item already exists");
                        return;
                      }
                    }

                    const labor = Number(itemEditor.labor_minutes || 0);
                    const bench = Number(itemEditor.bench_copper || 0);
                    const fullOverrideCopper = parseCurrency(itemEditor.full_service_display || "");

                    if (!name.trim()) {
                      alert("Name is required");
                      return;
                    }

                    let nextCustomItems = settings.custom_items || [];
                    let nextMeta = settings.item_meta_overrides || {};
                    let nextFull = { ...(settings.full_service_overrides || {}) };

                    if (itemEditor.mode === "add") {
                      const newItem = {
                        id,
                        name,
                        category,
                        tier,
                        base_unit_price_copper: bench,
                        craft_time_mins: labor,
                      };
                      nextCustomItems = [...nextCustomItems, newItem];
                    } else {
                      const isCustom = (settings.custom_items || []).some((ci) => ci.id === itemEditor.id);
                      if (isCustom) {
                        nextCustomItems = (settings.custom_items || []).map((ci) =>
                          ci.id === itemEditor.id
                            ? {
                                ...ci,
                                name,
                                category,
                                tier,
                                base_unit_price_copper: bench,
                                craft_time_mins: labor,
                              }
                            : ci,
                        );
                      } else {
                        nextMeta = { ...nextMeta };
                        nextMeta[itemEditor.id] = {
                          ...(nextMeta[itemEditor.id] || {}),
                          name,
                          category,
                          tier,
                          base_unit_price_copper: bench,
                          craft_time_mins: labor,
                        };
                      }
                    }

                    if (fullOverrideCopper > 0) {
                      nextFull[id] = fullOverrideCopper;
                    } else {
                      delete nextFull[id];
                    }

                    // Optimistically update local state
                    setSettings((prev) => ({
                      ...prev,
                      custom_items: nextCustomItems,
                      item_meta_overrides: nextMeta,
                      full_service_overrides: nextFull,
                    }));

                    // Persist to backend so buyer dropdown/quotes see the change
                    try {
                      await axios.patch(`${API}/apothecary/admin/settings`, {
                        labor_rate_cpm: Number(settings.labor_rate_cpm),
                        markup_pct: Number(settings.markup_pct),
                        disabled_rarities: settings.disabled_rarities || {},
                        disabled_tiers: settings.disabled_tiers || {},
                        disabled_items: settings.disabled_items || [],
                        item_overrides: settings.item_overrides || {},
                        custom_items: nextCustomItems,
                        removed_items: settings.removed_items || [],
                        item_meta_overrides: nextMeta,
                        full_service_overrides: nextFull,
                      });
                      await loadSettings();
                      await loadCatalog();
                    } catch (e) {
                      console.error(e);
                      alert("Failed to save item");
                    }

                    setItemEditor(null);
                  }}
                  className="px-3 py-1 rounded-md bg-amber-700 hover:bg-amber-600 text-black text-xs font-semibold"
                >
                  Save item
                </button>
              </div>
            </div>
          )}

          {recipeEditorItem && (
            <div className="fixed inset-0 z-40 flex items-start justify-end bg-black/40">
              <div className="w-full max-w-md h-full bg-[#120d0a] border-l border-amber-900/70 shadow-xl p-4 overflow-y-auto">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="text-amber-100 font-semibold text-base truncate">{recipeEditorItem.name}</div>
                    <div className="flex items-center gap-2 text-xs text-amber-300/80 mt-1">
                      <span>{recipeEditorItem.category} – {recipeEditorItem.tier}</span>
                      <span className="px-1.5 py-0.5 rounded-full border border-amber-700 text-amber-300 font-mono text-[10px]">
                        {recipeEditorItem.id}
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setRecipeEditorItem(null)}
                    className="text-amber-300 hover:text-amber-100 text-xs"
                  >
                    Close
                  </button>
                </div>

                <div className="space-y-4 text-xs">
                  {(() => {
                    const customMatch = (settings.custom_items || []).find(
                      (ci) => ci.id === recipeEditorItem.id,
                    );
                    const isCustomRecipeItem = Boolean(customMatch);

                    return (
                      <>
                        <div className="space-y-2">
                          <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-amber-200 mb-1">Category</label>
                        <input
                          type="text"
                          value={recipeEditorItem.category}
                          readOnly
                          className="w-full bg-[#17110e] border border-[#4b3624] text-amber-100 rounded px-2 py-1 text-xs opacity-70"
                        />
                      </div>
                      <div>
                        <label className="block text-amber-200 mb-1">Tier</label>
                        <select
                          value={settings.item_meta_overrides?.[recipeEditorItem.id]?.tier ?? recipeEditorItem.tier}
                          onChange={(e) => {
                            const tier = e.target.value;
                            const nextMeta = { ...(settings.item_meta_overrides || {}) };
                            nextMeta[recipeEditorItem.id] = {
                              ...(nextMeta[recipeEditorItem.id] || {}),
                              tier,
                            };
                            handleSettingsChange("item_meta_overrides", nextMeta);
                          }}
                          className="w-full bg-[#17110e] border border-[#4b3624] text-amber-100 rounded px-2 py-1 text-xs"
                        >
                          {tierOptions.map((t) => (
                            <option key={t} value={t}>
                              {t}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {!isCustomRecipeItem && (
                      <div>
                        <label className="block text-amber-200 mb-1">Name</label>
                        <input
                          type="text"
                          value={settings.item_meta_overrides?.[recipeEditorItem.id]?.name ?? recipeEditorItem.name}
                          onChange={(e) => {
                            const name = e.target.value;
                            const nextMeta = { ...(settings.item_meta_overrides || {}) };
                            nextMeta[recipeEditorItem.id] = {
                              ...(nextMeta[recipeEditorItem.id] || {}),
                              name,
                            };
                            handleSettingsChange("item_meta_overrides", nextMeta);
                          }}
                          className="w-full bg-[#17110e] border border-[#4b3624] text-amber-100 rounded px-2 py-1 text-xs"
                        />
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-amber-200 mb-1">Bench cost (copper)</label>
                        <input
                          type="number"
                          value={settings.item_meta_overrides?.[recipeEditorItem.id]?.base_unit_price_copper ?? recipeEditorItem.base_unit_price_copper ?? ""}
                          onChange={(e) => {
                            const v = Number(e.target.value || 0);
                            const nextMeta = { ...(settings.item_meta_overrides || {}) };
                            nextMeta[recipeEditorItem.id] = {
                              ...(nextMeta[recipeEditorItem.id] || {}),
                              base_unit_price_copper: v,
                            };
                            handleSettingsChange("item_meta_overrides", nextMeta);
                          }}
                          className="w-full bg-[#17110e] border border-[#4b3624] text-amber-100 rounded px-2 py-1 text-xs"
                        />
                      </div>
                      <div>
                        <label className="block text-amber-200 mb-1">Markup % override</label>
                        <input
                          type="number"
                          step="0.01"
                          value={settings.item_markup_overrides?.[recipeEditorItem.id] ?? ""}
                          onChange={(e) => {
                            const raw = e.target.value;
                            const next = { ...(settings.item_markup_overrides || {}) };
                            if (!raw) {
                              delete next[recipeEditorItem.id];
                            } else {
                              next[recipeEditorItem.id] = Number(raw);
                            }
                            handleSettingsChange("item_markup_overrides", next);
                          }}
                          placeholder={String(settings.markup_pct)}
                          className="w-full bg-[#17110e] border border-[#4b3624] text-amber-100 rounded px-2 py-1 text-xs"
                        />
                      </div>
                    </div>
                  </div>

                      </>
                    );
                  })()}

                  {/* Recipe editor (dynamic TRUE COST recipe) */}
                  <div className="pt-3 border-t border-amber-900/60 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-amber-200 font-semibold text-xs">TRUE COST recipe</span>
                      <button
                        type="button"
                        onClick={() => {
                          const current = (settings.recipes || {})[recipeEditorItem.id] || { bench: 0, ingredients: [] };
                          const next = { ...(settings.recipes || {}), [recipeEditorItem.id]: {
                            ...current,
                            ingredients: [...(current.ingredients || []), { kind: "reagent", name: "", qty: 1 }],
                          }};
                          handleSettingsChange("recipes", next);
                        }}
                        className="text-[11px] text-amber-300 hover:text-amber-100"
                      >
                        + Add ingredient
                      </button>
                    </div>
                    <p className="text-[11px] text-amber-300/80">
                      Define bench and ingredients to enable TRUE COST for this item. Reagent names must match reagent costs (case-insensitive, spacing-sensitive).
                    </p>

                    <div className="space-y-2">
                      <div>
                        <label className="block text-amber-200 mb-1">Bench (copper)</label>
                        <input
                          type="number"
                          value={(settings.recipes || {})[recipeEditorItem.id]?.bench ?? ""}
                          onChange={(e) => {
                            const v = Number(e.target.value || 0);
                            const current = (settings.recipes || {})[recipeEditorItem.id] || { bench: 0, ingredients: [] };
                            const next = { ...(settings.recipes || {}), [recipeEditorItem.id]: {
                              ...current,
                              bench: v,
                            }};
                            handleSettingsChange("recipes", next);
                          }}
                          className="w-full bg-[#17110e] border border-[#4b3624] text-amber-100 rounded px-2 py-1 text-xs"
                        />
                      </div>

                      <div className="space-y-1">
                        {((settings.recipes || {})[recipeEditorItem.id]?.ingredients || []).map((ing, idx) => (
                          <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                            <select
                              className="col-span-3 bg-[#17110e] border border-[#4b3624] text-amber-100 rounded px-2 py-1 text-xs"
                              value={ing.kind || "reagent"}
                              onChange={(e) => {
                                const kind = e.target.value;
                                const current = (settings.recipes || {})[recipeEditorItem.id] || { bench: 0, ingredients: [] };
                                const ingredients = [...(current.ingredients || [])];
                                ingredients[idx] = { ...ingredients[idx], kind };
                                const next = { ...(settings.recipes || {}), [recipeEditorItem.id]: {
                                  ...current,
                                  ingredients,
                                }};
                                handleSettingsChange("recipes", next);
                              }}
                            >
                              <option value="reagent">Reagent</option>
                              <option value="craft">Craft</option>
                            </select>
                            <input
                              className="col-span-6 bg-[#17110e] border border-[#4b3624] text-amber-100 rounded px-2 py-1 text-xs"
                              type="text"
                              placeholder={ing.kind === "craft" ? "Craft item id (e.g. magic_powder)" : "Reagent name (e.g. Raw Honey)"}
                              value={ing.name || ""}
                              onChange={(e) => {
                                const name = e.target.value;
                                const current = (settings.recipes || {})[recipeEditorItem.id] || { bench: 0, ingredients: [] };
                                const ingredients = [...(current.ingredients || [])];
                                ingredients[idx] = { ...ingredients[idx], name };
                                const next = { ...(settings.recipes || {}), [recipeEditorItem.id]: {
                                  ...current,
                                  ingredients,
                                }};
                                handleSettingsChange("recipes", next);
                              }}
                            />
                            <input
                              className="col-span-2 bg-[#17110e] border border-[#4b3624] text-amber-100 rounded px-2 py-1 text-xs"
                              type="number"
                              min="1"
                              value={ing.qty ?? 1}
                              onChange={(e) => {
                                const qty = Number(e.target.value || 1);
                                const current = (settings.recipes || {})[recipeEditorItem.id] || { bench: 0, ingredients: [] };
                                const ingredients = [...(current.ingredients || [])];
                                ingredients[idx] = { ...ingredients[idx], qty };
                                const next = { ...(settings.recipes || {}), [recipeEditorItem.id]: {
                                  ...current,
                                  ingredients,
                                }};
                                handleSettingsChange("recipes", next);
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => {
                                const current = (settings.recipes || {})[recipeEditorItem.id] || { bench: 0, ingredients: [] };
                                const ingredients = [...(current.ingredients || [])];
                                ingredients.splice(idx, 1);
                                const nextRecipes = { ...(settings.recipes || {}), [recipeEditorItem.id]: {
                                  ...current,
                                  ingredients,
                                }};
                                handleSettingsChange("recipes", nextRecipes);
                              }}
                              className="col-span-1 text-red-400 hover:text-red-200 text-xs"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>

                      {((settings.recipes || {})[recipeEditorItem.id]?.ingredients || []).length > 0 && (
                        <button
                          type="button"
                          onClick={() => {
                            const next = { ...(settings.recipes || {}) };
                            delete next[recipeEditorItem.id];
                            handleSettingsChange("recipes", next);
                          }}
                          className="mt-2 text-[11px] text-red-400 hover:text-red-200"
                        >
                          Remove recipe (fallback to static/legacy)
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="pt-3 border-t border-amber-900/60">
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          await axios.patch(`${API}/apothecary/admin/settings`, {
                            labor_rate_cpm: Number(settings.labor_rate_cpm),
                            markup_pct: Number(settings.markup_pct),
                            disabled_rarities: settings.disabled_rarities || {},
                            disabled_tiers: settings.disabled_tiers || {},
                            disabled_items: settings.disabled_items || [],
                            item_overrides: settings.item_overrides || {},
                            custom_items: settings.custom_items || [],
                            removed_items: settings.removed_items || [],
                            item_meta_overrides: settings.item_meta_overrides || {},
                            full_service_overrides: settings.full_service_overrides || {},
                            ingredient_costs: settings.ingredient_costs || {},
                            item_markup_overrides: settings.item_markup_overrides || {},
                            recipes: settings.recipes || {},
                          });
                          await loadSettings();
                          await loadCatalog();
                          setRecipeEditorItem(null);
                        } catch (e) {
                          console.error(e);
                          alert("Failed to save item");
                        }
                      }}
                      className="inline-flex items-center justify-center rounded-md bg-amber-700 hover:bg-amber-600 text-black px-4 py-2 text-xs font-semibold"
                    >
                      Save item
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <p className="text-xs text-amber-300/80">Manage visibility and metadata by profession and tier.</p>

          <button
            onClick={saveSettings}
            disabled={savingSettings}
            className="mt-2 inline-flex items-center justify-center rounded-md bg-amber-700 hover:bg-amber-600 text-black px-4 py-2 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {savingSettings ? "Saving..." : "Save settings"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Orders */}
      <section className="bg-[radial-gradient(circle_at_top,_#231712_0%,_#110c0a_50%,_#060505_100%)] border border-[#3c2a1c]/70 rounded-xl p-4 shadow-[0_18px_40px_rgba(0,0,0,0.85)]">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-lg font-semibold text-amber-100 tracking-wide">Apothecary orders</h2>
            <p className="text-xs text-amber-300/80">Who ordered, where, and current status.</p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={loadOrders}
              disabled={loading}
              className="rounded-md bg-amber-700 hover:bg-amber-600 text-black px-3 py-1 text-xs font-semibold disabled:opacity-60"
            >
              {loading ? "Refreshing..." : "Refresh"}
            </button>
            <button
              type="button"
              onClick={logout}
              className="rounded-md bg-red-700/80 hover:bg-red-600 text-amber-50 px-3 py-1 text-xs font-semibold"
            >
              Log out
            </button>
          </div>
        </div>

        <div className="overflow-x-auto rounded-lg border border-amber-900/70 bg-black/20">
          {activeOrders.length === 0 ? (
            <div className="px-4 py-6 text-center text-amber-200 text-sm">No active Apothecary orders.</div>
          ) : (
            <table className="min-w-full text-xs">
              <thead>
                <tr className="bg-black/40 text-amber-300">
                  <th className="px-2 py-2 text-left">Order #</th>
                  <th className="px-2 py-2 text-left">When</th>
                  <th className="px-2 py-2 text-left">Server</th>
                  <th className="px-2 py-2 text-left">Character</th>
                  <th className="px-2 py-2 text-left">Contact</th>
                  <th className="px-2 py-2 text-left">Items</th>
                  <th className="px-2 py-2 text-left">Status</th>
                  <th className="px-2 py-2 text-left">Discount</th>
                  <th className="px-2 py-2 text-right">Buyer total</th>
                  <th className="px-2 py-2 text-right">After discount</th>
                  <th className="px-2 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {activeOrders.map((o) => (
                  <tr key={o.id} className="border-t border-amber-900/60">
                    <td className="px-2 py-2 text-amber-100/90 font-mono">
                      {o.order_number || (o.id ? o.id.slice(-6).toUpperCase() : "")}
                    </td>
                    <td className="px-2 py-2 text-amber-100/90">
                      {o.created_at ? new Date(o.created_at).toLocaleString() : ""}
                    </td>
                    <td className="px-2 py-2 text-amber-100/90">{o.server}</td>
                    <td className="px-2 py-2 text-amber-100/90">{o.character}</td>
                    <td className="px-2 py-2 text-amber-100/90">{o.contact || ""}</td>
                    <td className="px-2 py-2 text-amber-100/90 align-top">
                      {o.lines && o.lines.length > 0 ? (
                        <div className="space-y-0.5">
                          {o.lines.map((ln, idx) => (
                            <div
                              key={idx}
                              className="flex flex-wrap items-center gap-1 text-[11px] text-amber-200/90"
                            >
                              <span className="font-semibold">{ln.item_name}</span>
                              <span className="text-amber-300/80">• {ln.rarity} × {ln.quantity}</span>
                              {ln.materials_provided && (
                                <span className="inline-flex items-center rounded-full border border-emerald-500/70 bg-emerald-900/40 px-1.5 py-0.5 text-[9px] text-emerald-200">
                                  mats provided
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-[11px] text-amber-300/70">No line details</span>
                      )}
                    </td>
                    <td className="px-2 py-2">
                      <select
                        value={o.status}
                        onChange={(e) => updateOrder(o.id, { status: e.target.value })}
                        className="bg-[#17110e] border border-[#4b3624] text-amber-100 text-[11px] rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-amber-500"
                      >
                        {statusOptions.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-2 py-2 align-top">
                      <div className="flex flex-col gap-1">
                        <select
                          value={o.customer_type}
                          onChange={(e) => {
                            const value = e.target.value;
                            const patch = { customer_type: value };
                            // Reset custom fields when leaving Custom
                            if (value !== "Custom") {
                              patch.discount_pct = undefined;
                              patch.discount_note = "";
                            }
                            updateOrder(o.id, patch);
                          }}
                          className="bg-[#17110e] border border-[#4b3624] text-amber-100 text-[11px] rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-amber-500"
                        >
                          {discountOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                        {o.customer_type === "Custom" && (
                          <div className="space-y-1 text-[10px] text-amber-200">
                            <div className="flex items-center gap-1">
                              <span className="whitespace-nowrap">Custom %:</span>
                              <input
                                type="number"
                                min="0"
                                max="100"
                                value={o.discount_pct ?? 0}
                                onChange={(e) =>
                                  updateOrder(o.id, {
                                    discount_pct: Number(e.target.value || 0),
                                  })
                                }
                                className="w-16 bg-[#17110e] border border-[#4b3624] text-amber-100 rounded px-1 py-0.5 text-[10px]"
                              />
                            </div>
                            <div className="flex flex-col gap-0.5">
                              <span className="whitespace-nowrap">Reason:</span>
                              <textarea
                                rows={2}
                                value={o.discount_note || ""}
                                onChange={(e) =>
                                  updateOrder(o.id, {
                                    discount_note: e.target.value,
                                  })
                                }
                                placeholder="Why this discount?"
                                className="w-full bg-[#17110e] border border-[#4b3624] text-amber-100 rounded px-1 py-0.5 text-[10px] resize-none"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-2 py-2 text-right text-amber-100/90">
                      {formatCopper(o.buyer_total_copper)}
                      {typeof o.discount_pct === "number" && o.discount_pct > 0 && (
                        <span className="block text-[10px] text-amber-300/80">
                          {o.discount_pct}% off
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-right text-amber-100/90">{formatCopper(o.admin_total_copper)}</td>
                    <td className="px-2 py-2 text-right space-x-1">
                      <button
                        type="button"
                        onClick={() => updateOrder(o.id, { status: "Completed" })}
                        className="rounded-md bg-emerald-700 hover:bg-emerald-600 text-emerald-50 px-2 py-1 text-[11px] font-semibold"
                      >
                        Mark completed
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          if (!window.confirm("Delete this order? This cannot be undone.")) return;
                          try {
                            await axios.delete(`${API}/apothecary/admin/orders/${o.id}`);
                            await loadOrders();
                          } catch (e) {
                            console.error(e);
                            alert("Failed to delete order");
                          }
                        }}
                        className="rounded-md bg-red-800/80 hover:bg-red-700 text-amber-50 px-2 py-1 text-[11px] font-semibold"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Completed orders (collapsible) */}
        {completedOrders.length > 0 && (
          <details className="mt-4 border border-amber-900/70 rounded-lg bg-black/20">
            <summary className="cursor-pointer select-none px-3 py-2 text-xs text-amber-200 flex items-center justify-between">
              <span className="font-semibold">Completed orders ({completedOrders.length})</span>
              <span className="text-amber-300/80 text-[11px]">Click to expand</span>
            </summary>
            <div className="overflow-x-auto border-t border-amber-900/70">
              <table className="min-w-full text-xs">
                <thead>
                  <tr className="bg-black/40 text-amber-300">
                    <th className="px-2 py-2 text-left">Order #</th>
                    <th className="px-2 py-2 text-left">When</th>
                    <th className="px-2 py-2 text-left">Server</th>
                    <th className="px-2 py-2 text-left">Character</th>
                    <th className="px-2 py-2 text-left">Contact</th>
                    <th className="px-2 py-2 text-left">Items</th>
                    <th className="px-2 py-2 text-left">Status</th>
                    <th className="px-2 py-2 text-left">Discount</th>
                    <th className="px-2 py-2 text-right">Buyer total</th>
                    <th className="px-2 py-2 text-right">After discount</th>
                    <th className="px-2 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {completedOrders.map((o) => (
                    <tr key={o.id} className="border-t border-amber-900/60">
                      <td className="px-2 py-2 text-amber-100/90 font-mono">
                        {o.order_number || (o.id ? o.id.slice(-6).toUpperCase() : "")}
                      </td>
                      <td className="px-2 py-2 text-amber-100/90">
                        {o.created_at ? new Date(o.created_at).toLocaleString() : ""}
                      </td>
                      <td className="px-2 py-2 text-amber-100/90">{o.server}</td>
                      <td className="px-2 py-2 text-amber-100/90">{o.character}</td>
                      <td className="px-2 py-2 text-amber-100/90">{o.contact || ""}</td>
                      <td className="px-2 py-2 text-amber-100/90 align-top">
                        {o.lines && o.lines.length > 0 ? (
                          <div className="space-y-0.5">
                            {o.lines.map((ln, idx) => (
                              <div
                                key={idx}
                                className="flex flex-wrap items-center gap-1 text-[11px] text-amber-200/90"
                              >
                                <span className="font-semibold">{ln.item_name}</span>
                                <span className="text-amber-300/80">• {ln.rarity} × {ln.quantity}</span>
                                {ln.materials_provided && (
                                  <span className="inline-flex items-center rounded-full border border-emerald-500/70 bg-emerald-900/40 px-1.5 py-0.5 text-[9px] text-emerald-200">
                                    mats provided
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="text-[11px] text-amber-300/70">No line details</span>
                        )}
                      </td>
                      <td className="px-2 py-2">
                        <select
                          value={o.status}
                          onChange={(e) => updateOrder(o.id, { status: e.target.value })}
                          className="bg-[#17110e] border border-[#4b3624] text-amber-100 text-[11px] rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-amber-500"
                        >
                          {statusOptions.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-2 py-2">
                        <select
                          value={o.customer_type}
                          onChange={(e) => updateOrder(o.id, { customer_type: e.target.value })}
                          className="bg-[#17110e] border border-[#4b3624] text-amber-100 text-[11px] rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-amber-500"
                        >
                          {discountOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-2 py-2 text-right text-amber-100/90">{formatCopper(o.buyer_total_copper)}</td>
                      <td className="px-2 py-2 text-right text-amber-100/90">{formatCopper(o.admin_total_copper)}</td>
                      <td className="px-2 py-2 text-right space-x-1">
                        <button
                          type="button"
                          onClick={async () => {
                            if (!window.confirm("Delete this order? This cannot be undone.")) return;
                            try {
                              await axios.delete(`${API}/apothecary/admin/orders/${o.id}`);
                              await loadOrders();
                            } catch (e) {
                              console.error(e);
                              alert("Failed to delete order");
                            }
                          }}
                          className="rounded-md bg-red-800/80 hover:bg-red-700 text-amber-50 px-2 py-1 text-[11px] font-semibold"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        )}
      </section>

      {/* Shop settings + materials calculator row */}
      <div className="mt-6 flex flex-col xl:flex-row gap-6 items-start">
        {/* Shop settings */}
        <section className="flex-1 max-w-xl bg-[radial-gradient(circle_at_top,_#241612_0%,_#110c0a_50%,_#060505_100%)] border border-[#3c2a1c]/70 rounded-xl p-4 shadow-[0_18px_40px_rgba(0,0,0,0.85)]">
          <h3 className="text-lg font-semibold text-amber-100 mb-2 tracking-wide">Shop settings</h3>
          {settingsContent}
        </section>

        {/* Materials calculator panel */}
        <section className="w-full xl:max-w-sm bg-[radial-gradient(circle_at_top,_#241612_0%,_#110c0a_50%,_#060505_100%)] border border-[#3c2a1c]/70 rounded-xl p-4 shadow-[0_18px_40px_rgba(0,0,0,0.85)]">
          <h3 className="text-lg font-semibold text-amber-100 mb-2 tracking-wide">Materials calculator</h3>
          <p className="text-xs text-amber-300/80 mb-3">
            Compute reagents &amp; sub-crafts needed for Alchemy &amp; Scribing items using TRUE COST recipes.
          </p>

          <div className="flex flex-col gap-3 text-xs">
            {/* Filters */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-amber-200">Filter:</span>
              <button
                type="button"
                onClick={() => setMaterialCalcCategory("All")}
                className={`px-2 py-1 rounded-full border text-[11px] ${
                  materialCalcCategory === "All"
                    ? "bg-amber-700 text-black border-amber-500"
                    : "bg-[#2a1d17] text-amber-200 border-amber-800 hover:bg-amber-900/40"
                }`}
              >
                All
              </button>
              <button
                type="button"
                onClick={() => setMaterialCalcCategory("Alchemy")}
                className={`px-2 py-1 rounded-full border text-[11px] ${
                  materialCalcCategory === "Alchemy"
                    ? "bg-amber-700 text-black border-amber-500"
                    : "bg-[#2a1d17] text-amber-200 border-amber-800 hover:bg-amber-900/40"
                }`}
              >
                Alchemy
              </button>
              <button
                type="button"
                onClick={() => setMaterialCalcCategory("Scribing")}
                className={`px-2 py-1 rounded-full border text-[11px] ${
                  materialCalcCategory === "Scribing"
                    ? "bg-amber-700 text-black border-amber-500"
                    : "bg-[#2a1d17] text-amber-200 border-amber-800 hover:bg-amber-900/40"
                }`}
              >
                Scribing
              </button>

              <input
                type="text"
                placeholder="Search by name…"
                value={materialCalcSearch}
                onChange={(e) => setMaterialCalcSearch(e.target.value)}
                className="flex-1 min-w-[120px] bg-[#17110e] border border-[#4b3624] text-amber-100 rounded px-2 py-1 text-[11px]"
              />
            </div>

            {/* Item + quantity + action */}
            <div className="flex flex-col gap-2">
              <select
                className="w-full bg-[#17110e] border border-[#4b3624] text-amber-100 rounded px-2 py-1 text-xs"
                value={materialCalcItemId}
                onChange={(e) => setMaterialCalcItemId(e.target.value)}
              >
                <option value="">Select an item…</option>
                {catalog
                  .filter((it) => it.category === "Alchemy" || it.category === "Scribing")
                  .filter((it) =>
                    materialCalcCategory === "All" ? true : it.category === materialCalcCategory,
                  )
                  .filter((it) =>
                    !materialCalcSearch
                      ? true
                      : it.name.toLowerCase().includes(materialCalcSearch.toLowerCase()),
                  )
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((it) => (
                    <option key={it.id} value={it.id}>
                      {it.name} ({it.category})
                    </option>
                  ))}
              </select>

              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="1"
                  value={materialCalcQty}
                  onChange={(e) => setMaterialCalcQty(e.target.value)}
                  className="w-24 bg-[#17110e] border border-[#4b3624] text-amber-100 rounded px-2 py-1 text-xs"
                />
                <button
                  type="button"
                  onClick={runMaterialCalc}
                  disabled={!materialCalcItemId || materialCalcLoading}
                  className="px-3 py-1 rounded-md bg-amber-700 hover:bg-amber-600 text-black text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {materialCalcLoading ? "Calculating…" : "Calculate"}
                </button>
              </div>
            </div>

            <label className="inline-flex items-center gap-1 text-[11px] text-amber-300/80">
              <input
                type="checkbox"
                checked={materialCalcFull}
                onChange={(e) => setMaterialCalcFull(e.target.checked)}
                className="h-3 w-3"
              />
              <span>Include sub-recipes (show full tree)</span>
            </label>

            {materialCalcError && (
              <p className="text-red-400 text-[11px]">{materialCalcError}</p>
            )}

            {materialCalcResult && (
              <div className="border border-amber-900/60 rounded-md bg-black/30 p-2 text-[11px] space-y-2 max-h-56 overflow-auto">
                {materialCalcResult.reagents?.length ? (
                  <div>
                    <p className="font-semibold text-amber-200 mb-1">Reagents</p>
                    <ul className="space-y-0.5">
                      {materialCalcResult.reagents.map((r) => (
                        <li key={`reagent-${r.name}`} className="flex justify-between gap-2">
                          <span className="truncate">{r.name}</span>
                          <span className="text-amber-300/80 font-mono">x{r.qty}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <p className="text-amber-300/80">No reagents required.</p>
                )}

                {materialCalcResult.crafts?.length ? (
                  <div>
                    <p className="font-semibold text-amber-200 mt-2 mb-1">Crafted intermediates</p>
                    <ul className="space-y-0.5">
                      {materialCalcResult.crafts.map((c) => (
                        <li key={`craft-${c.name}`} className="flex justify-between gap-2">
                          <span className="truncate">{c.name}</span>
                          <span className="text-amber-300/80 font-mono">x{c.qty}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </section>
      </div>

      {/* Customer banner panel */}
      {settings && (
        <section className="mt-6 max-w-xl bg-[radial-gradient(circle_at_top,_#241612_0%,_#110c0a_50%,_#060505_100%)] border border-[#3c2a1c]/70 rounded-xl p-4 shadow-[0_18px_40px_rgba(0,0,0,0.85)]">
          <h3 className="text-lg font-semibold text-amber-100 mb-2 tracking-wide">Customer banner message</h3>
          <p className="text-xs text-amber-300/80 mb-2">
            This message is shown as a banner at the top of the Apothecary customer page.
          </p>
          <textarea
            value={settings.customer_notification || ""}
            onChange={(e) => handleSettingsChange("customer_notification", e.target.value)}
            rows={3}
            className="w-full bg-[#17110e] border border-[#4b3624] text-amber-100 rounded px-2 py-1 text-xs resize-y"
            placeholder="Optional message to show to customers (e.g., current lead times, special notes, closures)."
          />
          <p className="text-[11px] text-amber-400/80 mt-1">
            Leave blank to hide the banner. Basic text only; line breaks are preserved.
          </p>
        </section>
      )}
    </div>
  );
}
